import styled from "@emotion/styled";
import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, RouteComponentProps, Switch, withRouter } from "react-router-dom";
import AgentsRouter from "tsx/routes/agents";
import FullscreenWidgetsRouter from "tsx/routes/fullscreen-widgets";
import { Reports } from "tsx/routes/reports/Reports";
import SettingsRouter from "tsx/routes/settings";
import SubscriptionRouter from "tsx/routes/subscription";
import TicketsRouter from "tsx/routes/tickets";

import { DesignToken, RadiusToken, SpacingToken } from "@livechat/design-system-react-components";

import Redirect from "components/Redirect";
import { BackToTextApp } from "components/back-to-text-app/BackToTextApp";
import { LiveChatWidget } from "components/live-chat-widget/LiveChatWidget";
import { Context2025PromoModal } from "components/modals/new/Context2025PromoModal/Context2025PromoModal";
import { KbWidgetPreinstalledModal } from "components/modals/new/KbWidgetPreinstalledModal";
import Navigation from "components/navigation";
import { NotificationsBar } from "components/notifications-bar/NotificationsBar";
import ShortcutsModal from "components/shortcuts/ShortcutsModal";
import { ToastContainer } from "components/toast";
import { Cookie } from "constants/cookies";
import Role from "constants/role";
import { TEXT_APP_ROUTES } from "constants/text-app-routes";
import { pushDataLayerEvent } from "helpers/analytics";
import { initGoogleFont, isInIframe } from "helpers/app";
import { getCookie } from "helpers/cookies";
import { accountIsCanceled, getSubscriptionPhase, isExpired, isPaidSubscription } from "helpers/subscription";
import { IWithBreakpointsProps, withBreakpoints } from "helpers/withBreakpoints";
import { useAgentFlag } from "hooks/use-agent-flag";
import { useHasTextApp } from "hooks/use-has-text-app";
import { useIframeTheme } from "hooks/use-iframe-theme";
import { campaigns, useLimitedTimeCampaign } from "hooks/use-limited-time-campaigns";
import { prefetchMacros } from "hooks/use-macros";
import { useOrganization } from "hooks/use-organizations";
import { useShouldRedirectToTextApp } from "hooks/use-redirect-to-text-app";
import { useSetHelpDeskBotData } from "hooks/useSetHelpDeskBotData";
import { BrowserHistory } from "services/browser-history";
import {
  selectIsAdmin,
  selectLoggedInAgent,
  selectLoggedInAgentCompletedWelcomeTutorialFlag,
  selectLoggedInAgentPermission,
} from "store/entities/agents";
import { getFullscreenWidgets } from "store/entities/applications";
import { getBilling } from "store/entities/billing";
import { selectHasLiveChatIntegration, selectShopifyIntegration } from "store/entities/integrations";
import {
  ISetSourceFirstClick,
  LicenseActions,
  selectGoogleForwardingConfig,
  selectIsPastDue,
  selectLicenseId,
} from "store/entities/license";
import { selectCurrentSubscription } from "store/entities/subscriptions";
import { AppActions, selectCanAccessReports, selectCanAccessSettings } from "store/features/app";
import { SessionActions } from "store/features/session";
import { ToastHorizontalPosition, ToastVerticalPosition } from "store/features/toasts";
import { getKeepWelcomeTutorialOpened, selectSkipStandaloneAppTutorial } from "store/views/welcome-tutorial";
import { withVar } from "themes/helpers";

import { register } from "../../serviceWorkerRegistration";

import { Analytics } from "./Analytics";
import Automations from "./automations/Automations";
import { LiveChatForwardingInstructions } from "./external/LiveChatForwardingInstructions";
import { Marketplace } from "./marketplace";
import { SubscriptionPaymentIssueModal } from "./modals/SubscriptionPaymentIssueModal";
import { VerifyGoogleModal } from "./modals/VerifyGoogleModal";
import SubscriptionReminderModal from "./subscription/SubscriptionReminderModal";
import { UpsellModal } from "./subscription/UpsellModal";
import { WelcomeTutorial } from "./welcome-tutorial/WelcomeTutorial";
import { WelcomeTutorialRoutes } from "./welcome-tutorial/helpers";

interface IProps extends IWithBreakpointsProps, RouteComponentProps {}

const App: FC<IProps> = ({ history, isMobile }) => {
  const dispatch = useDispatch();

  const organizationQuery = useOrganization();
  const currentAgent = useSelector(selectLoggedInAgent);
  const subscription = useSelector(selectCurrentSubscription);
  const currentAgentPermission = useSelector(selectLoggedInAgentPermission);
  const currentAgentCompletedWelcomeTutorial = useSelector(selectLoggedInAgentCompletedWelcomeTutorialFlag);
  const hasLiveChatIntegration = useSelector(selectHasLiveChatIntegration);
  const isManagedByShopify = !!useSelector(selectShopifyIntegration);
  const billing = useSelector(getBilling);
  const accessReports = useSelector(selectCanAccessReports);
  const accessSettings = useSelector(selectCanAccessSettings);
  const fullscreenWidgets = useSelector(getFullscreenWidgets);
  const licenseId = useSelector(selectLicenseId);
  const googleForwardingConfig = useSelector(selectGoogleForwardingConfig);
  const isAdmin = useSelector(selectIsAdmin);
  const isPastDuePayment = useSelector(selectIsPastDue);
  const skipStandaloneAppTutorial = useSelector(selectSkipStandaloneAppTutorial);

  const hasGoogleForwardingConfig = !!googleForwardingConfig;

  const [shortcutsModalDisplayed, setShortcutsModalDisplayed] = useState(false);
  const [shouldShowSubscriptionPaymentIssueModal, setShouldShowSubscriptionPaymentIssueModal] = useState(
    isAdmin && isPastDuePayment
  );
  const { isCampaignActive: isContext2025PromoCampaignActive } = useLimitedTimeCampaign(
    campaigns.context2025PromoModal
  );
  const subscriptionPhase = getSubscriptionPhase(subscription, billing);
  const isPaidAccount = isPaidSubscription(subscriptionPhase);

  const [firstLoginDate, setFirstLoginDate] = useAgentFlag("firstLoginDate");

  const setSourceFirstClick = (payload: ISetSourceFirstClick): void => {
    dispatch(LicenseActions.setSourceFirstClick(payload));
  };

  const onLogout = (): void => {
    dispatch(SessionActions.logout());
  };

  const setFirstLoginDateAgentFlag = (): void => {
    if (!firstLoginDate) {
      setFirstLoginDate(new Date().toISOString());
    }
  };

  const handleCloseSubscriptionIssueModal = (): void => {
    setShouldShowSubscriptionPaymentIssueModal(false);
  };

  const [shouldShowVerifyGoogleModal, setShouldShowVerifyGoogleModal] = useState(isAdmin && hasGoogleForwardingConfig);

  const handleCloseVerifyGoogleModal = (): void => {
    setShouldShowVerifyGoogleModal(false);
  };

  useIframeTheme();

  useEffect(() => {
    pushDataLayerEvent("virtualPageView");
  }, [history.location.pathname]);

  useEffect(() => {
    if (isAdmin && hasGoogleForwardingConfig) {
      setShouldShowVerifyGoogleModal(true);
    }
  }, [isAdmin, googleForwardingConfig]);

  const setPartnerId = (partnerId: string): void => {
    dispatch(LicenseActions.setPartnerId({ partnerId }));
  };

  useEffect(() => {
    initGoogleFont();
    register(licenseId);
    prefetchMacros();

    const partnerId = getCookie(Cookie.PartnerId);
    if (partnerId) {
      setPartnerId(partnerId);
    }

    dispatch(AppActions.setInitialReferrer(location.pathname));

    const sourceFirstClick: ISetSourceFirstClick = {
      utmCampaign: getCookie(Cookie.UtmCampaign),
      utmContent: getCookie(Cookie.UtmContent),
      utmMedium: getCookie(Cookie.UtmMedium),
      utmSource: getCookie(Cookie.UtmSource),
      utmTerm: getCookie(Cookie.UtmTerm),
      referrer: getCookie(Cookie.Referrer),
      landingPage: getCookie(Cookie.LandingPage),
      sourceURL: getCookie(Cookie.SourceURL),
      sourceID: getCookie(Cookie.SourceID),
      sourceType: getCookie(Cookie.SourceType),
    };

    const utmSources: Partial<ISetSourceFirstClick> = {};
    Object.keys(sourceFirstClick).forEach(key => {
      const value = sourceFirstClick[key as keyof ISetSourceFirstClick];
      if (value !== null && value !== undefined) {
        utmSources[key as keyof ISetSourceFirstClick] = value.toString();
      }
    });

    if (Object.keys(utmSources).length > 0) {
      setSourceFirstClick(utmSources as ISetSourceFirstClick);
    }

    setFirstLoginDateAgentFlag();
  }, []);

  useEffect(() => {
    pushDataLayerEvent("virtualPageView");
  }, [history.location.pathname]);

  const toggleShortcutsModalDisplayed = (): void => {
    setShortcutsModalDisplayed(shortcutsModalDisplayed => !shortcutsModalDisplayed);
  };

  const embeddedInIframe = isInIframe();
  const accountCanceled = accountIsCanceled(billing);
  const subscriptionExpired = isExpired(subscription);
  const canAccessApp = (!accountCanceled || isManagedByShopify) && !subscriptionExpired;
  const hasTextApp = useHasTextApp();
  const shouldAllowUpsellModalDisplaying = !embeddedInIframe;

  const toastContainer = (
    <ToastContainer
      verticalPosition={ToastVerticalPosition.Top}
      horizontalPosition={ToastHorizontalPosition.Center}
      fixed
    />
  );

  const isChatWidgetEnabled = !embeddedInIframe && !isMobile;

  const isNotViewer = currentAgentPermission !== Role.Viewer;
  const accessMarketplace = isNotViewer;
  const accessAutomations = isNotViewer;
  const shouldSeeStandardWelcomeTutorial = !hasLiveChatIntegration && !skipStandaloneAppTutorial;
  const shouldSeeLiveChatIntegrationTutorial = hasLiveChatIntegration;

  const isOpenedInTextApp =
    embeddedInIframe && Object.values(TEXT_APP_ROUTES).some(route => history.location.pathname.startsWith(route));
  const keepWelcomeTutorialOpened = useSelector(getKeepWelcomeTutorialOpened);
  const mayForceTutorial =
    canAccessApp &&
    !currentAgentCompletedWelcomeTutorial &&
    !BrowserHistory.hasLocationEverChanged &&
    !isOpenedInTextApp;
  const shouldAllowWelcomeTutorialRoute =
    canAccessApp &&
    (!currentAgentCompletedWelcomeTutorial || keepWelcomeTutorialOpened) &&
    (shouldSeeStandardWelcomeTutorial || shouldSeeLiveChatIntegrationTutorial);

  const shouldRedirectToTextApp = useShouldRedirectToTextApp();
  const shouldForceOnboardingTutorial =
    mayForceTutorial && shouldSeeStandardWelcomeTutorial && isNotViewer && !shouldRedirectToTextApp;
  const shouldForceLiveChatIntegrationTutorial =
    mayForceTutorial && shouldSeeLiveChatIntegrationTutorial && !shouldRedirectToTextApp;
  const shouldAllowExternalLiveChatRoute = embeddedInIframe;
  const shouldForceForwardingProviders =
    history.location.pathname.endsWith("/external/forwarding") && shouldAllowExternalLiveChatRoute;
  const hideGlobalModals = isOpenedInTextApp;

  const isInOnboarding = shouldForceOnboardingTutorial || shouldForceLiveChatIntegrationTutorial;
  const hasNotCompletedTutorial = !currentAgentCompletedWelcomeTutorial;
  const shouldShowContext2025PromoModal =
    isContext2025PromoCampaignActive && isPaidAccount && !isInOnboarding && !hasNotCompletedTutorial && !isInIframe;

  useSetHelpDeskBotData();

  // wait for organization data to load
  // before launching onboarding logic which relies on it
  if (organizationQuery.isLoading) {
    return null;
  }

  if (shouldForceOnboardingTutorial) {
    return <Redirect to={WelcomeTutorialRoutes.WELCOME} />;
  }

  if (shouldForceLiveChatIntegrationTutorial && !shouldForceForwardingProviders) {
    return <Redirect to={WelcomeTutorialRoutes.LC_MANAGE_EMAILS} />;
  }

  return (
    <>
      {toastContainer}
      {isChatWidgetEnabled && <LiveChatWidget customerName={currentAgent.name} customerEmail={currentAgent.email} />}
      <Switch>
        {shouldAllowExternalLiveChatRoute && (
          <Route path="/external/forwarding" component={LiveChatForwardingInstructions} />
        )}
        {shouldForceForwardingProviders && <Redirect to="/external/forwarding" />};
        {shouldAllowWelcomeTutorialRoute && <Route path={WelcomeTutorialRoutes.WELCOME} component={WelcomeTutorial} />}
        <Route
          render={() => (
            <VerticalWrapper>
              {hasTextApp && !isMobile && !embeddedInIframe && <BackToTextApp />}
              <Wrapper isMobile={isMobile}>
                {!embeddedInIframe && (
                  <Navigation
                    agent={currentAgent}
                    canAccessApp={canAccessApp}
                    onLogout={onLogout}
                    billing={billing}
                    fullscreenWidgets={fullscreenWidgets}
                    toggleShortcutsModalDisplayed={toggleShortcutsModalDisplayed}
                  />
                )}
                <PageWrapper isMobile={isMobile}>
                  {canAccessApp && !embeddedInIframe && <NotificationsBar isMobile={isMobile} />}
                  <ContentWrapper isMobile={isMobile} embeddedInIframe={embeddedInIframe}>
                    <Switch>
                      <Route path="/agents" component={AgentsRouter} />
                      {canAccessApp && accessReports && <Route path="/reports" component={Reports} />}
                      <Route path="/subscription" component={SubscriptionRouter} />
                      {canAccessApp && accessMarketplace && <Route path="/marketplace*" component={Marketplace} />}
                      {canAccessApp && <Route path="/tickets" component={TicketsRouter} />}
                      {canAccessApp && accessSettings && <Route path="/settings" component={SettingsRouter} />}
                      {canAccessApp && <Route path="/apps" render={() => null} />}
                      {canAccessApp && accessAutomations && <Route path="/automations" component={Automations} />}
                      {canAccessApp && <Redirect to="/tickets" />}
                      {!canAccessApp && <Redirect to="/subscription" />}
                    </Switch>
                    {/* This route is needed to initialize the fullscreen apps and show notification badges on navigation */}
                    {canAccessApp && <Route component={FullscreenWidgetsRouter} />}
                  </ContentWrapper>
                </PageWrapper>
                {!hideGlobalModals && (
                  <>
                    <SubscriptionReminderModal />
                    <KbWidgetPreinstalledModal />
                  </>
                )}
              </Wrapper>
            </VerticalWrapper>
          )}
        />
      </Switch>
      {!hideGlobalModals && (
        <>
          <ShortcutsModal isOpen={shortcutsModalDisplayed} toggleOpen={toggleShortcutsModalDisplayed} />
          {shouldShowSubscriptionPaymentIssueModal && (
            <SubscriptionPaymentIssueModal onClose={handleCloseSubscriptionIssueModal} />
          )}
          {shouldShowVerifyGoogleModal && <VerifyGoogleModal onClose={handleCloseVerifyGoogleModal} />}
          {shouldAllowUpsellModalDisplaying && <UpsellModal />}
          {shouldShowContext2025PromoModal && <Context2025PromoModal />}
        </>
      )}
      <Analytics />
    </>
  );
};

const VerticalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: ${withVar(DesignToken.NavbarBackground)};
`;

const Wrapper = styled.div<{ isMobile: boolean }>`
  flex: 1;
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  ${({ isMobile }): string => (isMobile ? "flex-direction: column" : "")};
  background-color: ${withVar(DesignToken.NavbarBackground)};
`;

const PageWrapper = styled.div<{ isMobile: boolean }>`
  flex: 1;
  display: flex;
  ${({ isMobile }): string => (isMobile ? "order: -1" : "")};
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
`;

const ContentWrapper = styled.div<{ isMobile: boolean; embeddedInIframe: boolean }>`
  flex: 1;
  overflow: auto;
  border-radius: ${({ isMobile }): string =>
    isMobile ? `0 0 ${withVar(RadiusToken.Radius4)} ${withVar(RadiusToken.Radius4)}` : withVar(RadiusToken.Radius4)};
  background-color: ${withVar(DesignToken.SurfacePrimaryDefault)};
  margin: ${({ isMobile }): string =>
    isMobile ? `${withVar(SpacingToken.Spacing1)} ${withVar(SpacingToken.Spacing1)} 0` : "6px 6px 6px 0"};
  ${(props): string => (props.embeddedInIframe ? "margin: 0" : "")};
  ${(props): string => (props.embeddedInIframe ? "border-radius: 0" : "")};
`;

export default withRouter(withBreakpoints(App));
